<template>
  <div class="page-container">
    <div class="sidebar">
      <Sidebar />
    </div>
    <div class="content-wrapper">
      <!-- Alerts -->
      <div class="alerts-container">
        <div v-if="error" class="alert error">
          <i class="fas fa-exclamation-circle"></i>
          <span>{{ error }}</span>
          <button class="close-alert" @click="error = null">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="success" class="alert success">
          <i class="fas fa-check-circle"></i>
          <span>{{ success }}</span>
          <button class="close-alert" @click="success = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <h1><i class="fas fa-user-friends"></i> Personas</h1>
          <p>Manage and organize your personas</p>
        </div>
        <button @click="showCreateModal = true" class="primary-button">
          <i class="fas fa-plus"></i> New Persona
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading personas...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="main-content">
        <!-- Stats Overview -->
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-user-friends"></i>
            </div>
            <div class="stat-info">
              <h3>Total Personas</h3>
              <p>{{ personas.length }}</p>
            </div>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="search-container">
          <div class="search-bar">
            <i class="fas fa-search"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search personas..." 
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Personas Grid -->
        <div class="personas-grid">
          <div 
            v-for="persona in filteredPersonas" 
            :key="persona.id" 
            class="persona-card"
          >
            <div class="card-header">
              <div class="persona-info">
                <h3>{{ persona.name }}</h3>
                <span class="persona-id">ID: {{ persona.id }}</span>
              </div>
              <div class="card-actions">
                <button @click="editPersona(persona)" class="icon-button edit">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="confirmDelete(persona)" class="icon-button delete">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
            <div class="card-content">
              <p class="creation-date">Created: {{ formatDate(persona.created_at) }}</p>
              <p class="description">{{ persona.description }}</p>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-if="filteredPersonas.length === 0 && !loading" class="no-results">
          <i class="fas fa-search"></i>
          <h3>No personas found</h3>
          <p>Try adjusting your search criteria</p>
          <button @click="searchQuery = ''" class="secondary-button">Clear Search</button>
        </div>
      </div>

      <!-- Create Modal -->
      <div v-if="showCreateModal" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h2>Create New Persona</h2>
            <button @click="showCreateModal = false" class="close-modal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="personaName">Name</label>
              <input type="text" id="personaName" v-model="newPersona.name" placeholder="Enter persona name">
            </div>
            <div class="form-group">
              <label for="personaDescription">Description</label>
              <textarea id="personaDescription" v-model="newPersona.description" placeholder="Enter persona description"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showCreateModal = false" class="secondary-button">Cancel</button>
            <button @click="createPersona" :disabled="!newPersona.name" class="primary-button">
              <i v-if="creating" class="fas fa-spinner fa-spin"></i>
              <span v-else>Create Persona</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Edit Modal -->
      <div v-if="showEditModal" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h2>Edit Persona</h2>
            <button @click="showEditModal = false" class="close-modal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="editPersonaName">Name</label>
              <input type="text" id="editPersonaName" v-model="editingPersona.name" placeholder="Enter persona name">
            </div>
            <div class="form-group">
              <label for="editPersonaDescription">Description</label>
              <textarea id="editPersonaDescription" v-model="editingPersona.description" placeholder="Enter persona description"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showEditModal = false" class="secondary-button">Cancel</button>
            <button @click="updatePersona" :disabled="!editingPersona.name" class="primary-button">
              <i v-if="updating" class="fas fa-spinner fa-spin"></i>
              <span v-else>Update Persona</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Modal -->
      <div v-if="showDeleteModal" class="modal-overlay">
        <div class="modal-content delete-modal">
          <div class="modal-header">
            <h2>Delete Persona</h2>
            <button @click="showDeleteModal = false" class="close-modal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="delete-warning">
              <i class="fas fa-exclamation-triangle"></i>
              <h3>Are you sure?</h3>
              <p>You are about to delete the persona "{{ deletingPersona.name }}". This action cannot be undone.</p>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showDeleteModal = false" class="secondary-button">Cancel</button>
            <button @click="deletePersona" class="danger-button">
              <i v-if="deleting" class="fas fa-spinner fa-spin"></i>
              <span v-else>Delete Persona</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'PersonasManager',
  components: {
    Sidebar
  },
  data() {
    return {
      personas: [],
      loading: true,
      error: null,
      success: null,
      searchQuery: '',
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      creating: false,
      updating: false,
      deleting: false,
      newPersona: {
        name: '',
        description: ''
      },
      editingPersona: {
        id: null,
        name: '',
        description: ''
      },
      deletingPersona: {
        id: null,
        name: ''
      }
    };
  },
  computed: {
    filteredPersonas() {
      if (!this.searchQuery) {
        return this.personas;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.personas.filter(persona => 
        persona.name.toLowerCase().includes(query) || 
        persona.description.toLowerCase().includes(query)
      );
    }
  },
  created() {
    this.fetchPersonas();
  },
  methods: {
    async fetchPersonas() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          'https://api.kewaar.com/api/personas', 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token || '6179|jqJdzKjpICz4AJeeEtayyn8yoFISg6UdhKS6FTzRf7266f35'}`
            }
          }
        );
        
        this.personas = response.data.data;
      } catch (error) {
        this.error = 'Failed to fetch personas. Please try again.';
        console.error('Error fetching personas:', error);
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    async createPersona() {
      try {
        this.creating = true;
        const token = localStorage.getItem('token') || '6179|jqJdzKjpICz4AJeeEtayyn8yoFISg6UdhKS6FTzRf7266f35';
        
        const response = await axios.post(
          'https://api.kewaar.com/api/personas',
          this.newPersona,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Add new persona to the list
        this.personas.unshift(response.data.data);
        
        // Reset form and close modal
        this.newPersona = { name: '', description: '' };
        this.showCreateModal = false;
        this.success = 'Persona created successfully!';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.success = null;
        }, 3000);
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to create persona. Please try again.';
        console.error('Error creating persona:', error);
      } finally {
        this.creating = false;
      }
    },
    
    editPersona(persona) {
      this.editingPersona = {
        id: persona.id,
        name: persona.name,
        description: persona.description || ''
      };
      this.showEditModal = true;
    },
    
    async updatePersona() {
      try {
        this.updating = true;
        const token = localStorage.getItem('token') || '6179|jqJdzKjpICz4AJeeEtayyn8yoFISg6UdhKS6FTzRf7266f35';
        
        const response = await axios.put(
          `https://api.kewaar.com/api/personas/${this.editingPersona.id}`,
          {
            name: this.editingPersona.name,
            description: this.editingPersona.description
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Update the persona in the list
        const index = this.personas.findIndex(p => p.id === this.editingPersona.id);
        if (index !== -1) {
          this.personas[index] = response.data.data || {
            ...this.personas[index],
            name: this.editingPersona.name,
            description: this.editingPersona.description
          };
        }
        
        // Close modal and show success message
        this.showEditModal = false;
        this.success = 'Persona updated successfully!';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.success = null;
        }, 3000);
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to update persona. Please try again.';
        console.error('Error updating persona:', error);
      } finally {
        this.updating = false;
      }
    },
    
    confirmDelete(persona) {
      this.deletingPersona = {
        id: persona.id,
        name: persona.name
      };
      this.showDeleteModal = true;
    },
    
    async deletePersona() {
      try {
        this.deleting = true;
        const token = localStorage.getItem('token') || '6179|jqJdzKjpICz4AJeeEtayyn8yoFISg6UdhKS6FTzRf7266f35';
        
        await axios.delete(
          `https://api.kewaar.com/api/personas/${this.deletingPersona.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Remove the persona from the list
        this.personas = this.personas.filter(p => p.id !== this.deletingPersona.id);
        
        // Close modal and show success message
        this.showDeleteModal = false;
        this.success = 'Persona deleted successfully!';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.success = null;
        }, 3000);
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to delete persona. Please try again.';
        console.error('Error deleting persona:', error);
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
}

.sidebar {
  width: 250px;
  flex-shrink: 0;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.content-wrapper {
  flex-grow: 1;
  padding: 2rem;
  background-color: #f8fafc;
}

.alerts-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
}

.alert.error {
  background-color: #fee2e2;
  color: #dc2626;
}

.alert.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.close-alert {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-content h1 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-content p {
  color: #64748b;
  font-size: 0.875rem;
}

.primary-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.primary-button:hover {
  background-color: #2563eb;
}

.primary-button:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
}

.loading-spinner {
  border: 4px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 3rem;
  height: 3rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.stats-overview {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  max-width: 300px;
}

.stat-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  background-color: #e0f2fe;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stat-info h3 {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.stat-info p {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.search-container {
  margin-bottom: 2rem;
}

.search-bar {
  position: relative;
  max-width: 500px;
}

.search-bar i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background-color: white;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
}

.personas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.persona-card {
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.persona-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card-header {
  padding: 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.persona-info h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.persona-id {
  font-size: 0.75rem;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.icon-button {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.icon-button.edit {
  background-color: #e0f2fe;
  color: #0369a1;
}

.icon-button.edit:hover {
  background-color: #bae6fd;
}

.icon-button.delete {
  background-color: #fee2e2;
  color: #dc2626;
}

.icon-button.delete:hover {
  background-color: #fecaca;
}

.card-content {
  padding: 1.25rem;
}

.creation-date {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.description {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 0;
  text-align: center;
}

.no-results i {
  font-size: 3rem;
  color: #94a3b8;
  margin-bottom: 1rem;
}

.no-results h3 {
  font-size: 1.25rem;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.no-results p {
  color: #64748b;
  margin-bottom: 1.5rem;
}

.secondary-button {
  padding: 0.75rem 1.25rem;
  background-color: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.secondary-button:hover {
  background-color: #e2e8f0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background-color: white;
  border-radius: 0.75rem;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-modal {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;
}

.modal-body {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #475569;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.modal-footer {
  padding: 1.25rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.danger-button {
  padding: 0.75rem 1.25rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.danger-button:hover {
  background-color: #dc2626;
}

.delete-modal .modal-body {
  padding: 2rem 1.25rem;
}

.delete-warning {
  text-align: center;
}

.delete-warning i {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 1rem;
}

.delete-warning h3 {
  font-size: 1.25rem;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.delete-warning p {
  color: #64748b;
  margin-bottom: 0;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style> 